/**
 * Desktop keys: arrows or WASD walk, B opens the build menu, Enter places what I'm placing, Z zooms
 * out, Escape closes a sheet or stops placing.
 */
import type { Loop } from '@core/loop';
import { confirmPlacing, stopPlacing } from '@game/build/placement';
import type { Game } from '@game/game';
import { canWalk, closePanel, isFree, openPanel, toggleOverview } from '@game/view/panels';

const KEY_SPEED = 7;

export interface Keyboard {
  /** The turn the held keys ask for this frame, in pixels at 60 fps. */
  held(): { dx: number; dy: number };
  detach(): void;
}

export function attachKeyboard(canvas: HTMLCanvasElement, game: Game, loop: Loop): Keyboard {
  const keys = new Set<string>();
  const walkKeys = new Set(['arrowup', 'arrowdown', 'arrowleft', 'arrowright', 'w', 'a', 's', 'd']);

  const onKeyDown = (e: KeyboardEvent): void => {
    const target = e.target as HTMLElement | null;
    if (target && /INPUT|TEXTAREA/.test(target.tagName)) return;
    const s = game.store.get();
    const k = e.key.toLowerCase();
    if (s.panel !== 'none') {
      if (k === 'escape') {
        e.preventDefault();
        closePanel(game);
        return;
      }
      // a free sheet (the hub's, the build menu, info) leaves the planet free: walk on as ever
      if (!isFree(s.panel)) return;
    }
    const onPlanet = document.activeElement === canvas || document.activeElement === document.body;
    if (walkKeys.has(k)) {
      e.preventDefault();
      if (canWalk(game)) {
        game.local.walk = null;
        keys.add(k);
        loop.wake();
      }
    } else if (s.placing && k === 'escape') {
      e.preventDefault();
      stopPlacing(game);
    } else if (s.placing && k === 'enter' && onPlanet) {
      e.preventDefault();
      if (!e.repeat) confirmPlacing(game);
    } else if (k === 'z') toggleOverview(game);
    else if (k === 'b' && canWalk(game)) {
      if (s.panel === 'build') closePanel(game);
      else openPanel(game, 'build');
    }
  };
  const onKeyUp = (e: KeyboardEvent): void => {
    keys.delete(e.key.toLowerCase());
  };
  const onBlur = (): void => {
    keys.clear();
  };
  addEventListener('keydown', onKeyDown);
  addEventListener('keyup', onKeyUp);
  addEventListener('blur', onBlur);

  return {
    held() {
      let dx = 0;
      let dy = 0;
      if (keys.has('arrowleft') || keys.has('a')) dx += KEY_SPEED;
      if (keys.has('arrowright') || keys.has('d')) dx -= KEY_SPEED;
      if (keys.has('arrowup') || keys.has('w')) dy += KEY_SPEED;
      if (keys.has('arrowdown') || keys.has('s')) dy -= KEY_SPEED;
      if (dx || dy) loop.wake();
      return { dx, dy };
    },
    detach() {
      removeEventListener('keydown', onKeyDown);
      removeEventListener('keyup', onKeyUp);
      removeEventListener('blur', onBlur);
    },
  };
}
